"use client";

import { useState } from "react";

/** Private, so no caller's value can ever compare equal to it. */
const NOTHING_SEEN_YET = Symbol("useValueChanged.nothingSeenYet");

type UseValueChangedOptions = {
	/**
	 * Whether the first render counts as a change, when there is no render before
	 * it to compare against.
	 *
	 * Off by default — see admin-app `useValueChanged` for the full rationale.
	 */
	firstRenderCounts?: boolean;
};

/**
 * Returns `true` on the render where `value` differs from the render before it.
 *
 * Latch for React's "adjusting state when a prop changes" pattern.
 */
export function useValueChanged<T>(
	value: T,
	options?: UseValueChangedOptions,
): boolean {
	const [previous, setPrevious] = useState<T | typeof NOTHING_SEEN_YET>(() =>
		options?.firstRenderCounts ? NOTHING_SEEN_YET : value,
	);

	if (!Object.is(previous, value)) {
		setPrevious(value);
		return true;
	}

	return false;
}
